import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { SignOut } from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import { adminApi, getToken, clearToken, setUnauthorizedHandler, detail } from "@/lib/adminApi";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { Overview } from "@/components/admin/Overview";
import { Devices } from "@/components/admin/Devices";
import { QuickActivate } from "@/components/admin/QuickActivate";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const useNoIndex = () => useEffect(() => {
  const m = document.createElement("meta");
  m.name = "robots"; m.content = "noindex,nofollow";
  document.head.appendChild(m);
  const title = document.title;
  document.title = "Panel";
  return () => { m.remove(); document.title = title; };
}, []);

const TABS = [{ id: "overview", label: "Pregled" }, { id: "devices", label: "Uređaji" }];

const Dashboard = ({ username, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirm, setConfirm] = useState(null);

  const runAction = useCallback(async (call, msg) => {
    try {
      const r = await call();
      toast.success(msg);
      setRefreshKey((k) => k + 1);
      return r;
    } catch (e) { toast.error(detail(e)); return null; }
  }, []);
  const askConfirm = (c) => setConfirm({ ...c, onOk: () => { setConfirm(null); c.onOk(); } });

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-full-sm.png" alt="" className="h-11 w-11 object-contain" />
            <span className="hidden text-sm font-bold sm:inline">Admin panel</span>
          </div>
          <nav className="flex gap-1 rounded-full border border-line p-1">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-brand text-white" : "text-txt2 hover:text-white"}`}>{t.label}</button>
            ))}
          </nav>
          <button onClick={onLogout} className="flex items-center gap-1.5 text-sm text-txt2 hover:text-white" data-testid="admin-logout-btn" title={username}>
            <SignOut size={18} /><span className="hidden sm:inline">Odjava</span>
          </button>
        </div>
      </header>
      <main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6">
        <QuickActivate runAction={runAction} />
        {tab === "overview"
          ? <Overview refreshKey={refreshKey} />
          : <Devices refreshKey={refreshKey} askConfirm={askConfirm} runAction={runAction} />}
      </main>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default function AdminPanel() {
  useNoIndex();
  const [state, setState] = useState(getToken() ? "checking" : "login");
  const [username, setUsername] = useState("");

  const verify = useCallback(() => {
    setState("checking");
    adminApi.get("/admin/me")
      .then((r) => { setUsername(r.data.username); setState("ok"); })
      .catch(() => { clearToken(); setState("login"); });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => { setState("login"); toast.error("Sesija je istekla. Prijavite se ponovo."); });
    if (getToken()) verify();
  }, [verify]);

  const logout = () => { clearToken(); setState("login"); };

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      {state === "checking" && <div className="grid min-h-screen place-items-center bg-surface"><span className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" /></div>}
      {state === "login" && <AdminLogin onLogin={verify} />}
      {state === "ok" && <Dashboard username={username} onLogout={logout} />}
    </>
  );
}
